import {
  Entity,
  RELATION_CONSUMES_API,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { SONARQUBE_PROJECT_KEY_ANNOTATION } from '@backstage-community/plugin-sonarqube-react';
import { isSnykAvailable } from 'backstage-plugin-snyk';

/**
 * Annotation- and relation-based visibility helpers ported verbatim from the
 * legacy app (`src/components/catalog/utils.tsx`). They drive which entity
 * page tabs and overview cards are shown for a given entity.
 *
 * Helper NAMES are kept identical to the legacy app so behaviour is traceable.
 */

export const isSonarQubeAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[SONARQUBE_PROJECT_KEY_ANNOTATION]);

export const isArtifactoryAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['backstage.io/artifactory-id']);

export const isTechDocAvailable = (entity: Entity) =>
  Boolean(
    entity.metadata.annotations?.['backstage.io/techdocs-ref'] ||
      entity.metadata.annotations?.['backstage.io/techdocs-entity'],
  );

export const hasSubcomponents = (entity: Entity) =>
  Array.isArray(entity.relations) &&
  entity.relations.some(r => r.type === 'hasPart');

const hasProvidedApis = (entity: Entity): boolean =>
  !!entity.relations?.some(r => r.type === RELATION_PROVIDES_API);

const hasConsumedApis = (entity: Entity): boolean =>
  !!entity.relations?.some(r => r.type === RELATION_CONSUMES_API);

export const hasProvidedOrConsumedApis = (entity: Entity): boolean =>
  hasProvidedApis(entity) || hasConsumedApis(entity);

export function hasComponentLinks(entity: Entity): boolean {
  if (!entity || entity.kind?.toLowerCase() !== 'component') return false;
  const links =
    entity.metadata?.links ?? (entity as any).spec?.links ?? [];
  return Array.isArray(links) && links.length > 0;
}

export const isJFrogDontDisplay = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['jfrog.com/dont-display']);

export const isSnykDontDisplay = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['snyk.io/dont-display']);

export const isSonarQubeDontDisplay = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['sonarqube.org/dont-display']);

export const isTechDocsDontDisplay = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['backstage.io/techdocs-dont-display']);

export const isAPIDocsDontDisplay = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['backstage.io/apidocs-dont-display']);

export const isArtifactoryDontDisplay = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.['backstage.io/artifactory-dont-display']);

export const isSnykVisible = (entity: Entity) =>
  isSnykAvailable(entity) && !isSnykDontDisplay(entity);

